import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "context/UserContext";
import { useRouter } from "next/router";
import { validation } from "utils/helpers/validation";
import { z } from "zod";
import { useForm } from "@tanstack/react-form";

// Map backend signup error codes to translation keys
const createUserErrors: Record<string, string> = {
  isEmailExist: "signup_error.email_exists",
  isPhoneExist: "signup_error.phone_exists",
  default: "signup_error.generic",
};

export interface ISignupForm {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone: string;
  callingCode: string;
}

// Server error state (field-level translation keys)
type ISignupServerErrors = Partial<Record<keyof ISignupForm, string>>;

interface IProps {
  onSignupSuccess?: () => void;
  isRedirect?: boolean;
}

// Unicode letters with optional single spaces between parts (Arabic/Latin)
const unicodeNameWithSpaces = /^[\p{L}]+(?:\s[\p{L}]+)*$/u;

// Field-level zod schemas with translation-key messages
const firstNameSchema = z
  .string()
  .trim()
  .min(2, "first_name_valid_err")
  .regex(unicodeNameWithSpaces, "first_name_valid_err");

const lastNameSchema = z
  .string()
  .trim()
  .min(2, "last_name_valid_err")
  .regex(unicodeNameWithSpaces, "last_name_valid_err");

const emailSchema = z.string().trim().min(1, "email_valid_err").email("email_valid_err");

// Keep parity with existing password regex via utils.validation
const passwordSchema = z.string().trim().refine(validation.isPasswordValid, {
  message: "password_valid_err",
});

// Phone is 9 digits (without calling code), per existing regex
const phoneSchema = z.string().trim().refine(validation.isPhoneValid, {
  message: "phone_valid_err",
});

const callingCodeSchema = z.string().trim().min(1, "calling_code_valid_err");

// Full form schema (reduced)
const signupSchema = z.object({
  first_name: firstNameSchema,
  last_name: lastNameSchema,
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema,
  callingCode: callingCodeSchema,
});

const useSignup = ({ onSignupSuccess, isRedirect }: IProps) => {
  const { signup } = useUserContext();
  const { query, push } = useRouter();
  const { mutate, isPending: isLoading, data, error } = signup;

  // Track server-side field errors to surface alongside form validation
  const [serverErrors, setServerErrors] = useState<ISignupServerErrors>({});

  const form = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      callingCode: "966",
    },
    validators: {
      onSubmit: signupSchema,
    },
    onSubmit: async ({ value }) => {
      // Clear server errors before submission
      setServerErrors({});
      mutate(value);
    },
  });

  useEffect(() => {
    if (!error) return;

    // Backend may return "email" to indicate format issues from server side
    if (error === "email") {
      setServerErrors((prev) => ({ ...prev, email: "email_valid_err" }));
      return;
    }

    const tKey = createUserErrors[error as string] || createUserErrors.default;
    if (error === "isEmailExist") {
      setServerErrors((prev) => ({ ...prev, email: tKey }));
    } else if (error === "isPhoneExist") {
      setServerErrors((prev) => ({ ...prev, phone: tKey }));
    } else {
      setServerErrors((prev) => ({ ...prev, email: tKey }));
    }
  }, [error]);

  useEffect(() => {
    if (data?.message === "signupSuccessful") {
      if (onSignupSuccess) {
        onSignupSuccess();
      } else if (isRedirect) {
        const redirectPath = query.redirect ? query.redirect.toString() : "/";
        push(redirectPath);
      }
    }
  }, [data, push]);

  // Convenience helpers for per-field Zod validation on change/blur in the UI
  const fieldValidators = useMemo(
    () => ({
      first_name: firstNameSchema,
      last_name: lastNameSchema,
      email: emailSchema,
      password: passwordSchema,
      phone: phoneSchema,
      callingCode: callingCodeSchema,
    }),
    []
  );

  return {
    form,
    fieldValidators,
    serverErrors,
    isLoading,
    setServerErrors,
  };
};

export default useSignup;
